import React, { useCallback, useState } from 'react';

import type { UUID } from '@aline/common';
import { Select } from '@aline/common';
import { DndContext, DragOverlay, MeasuringStrategy } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { IconButton, Skeleton, Typography } from '@mui/material';
import { CirclePlus, LayoutList } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { makeStyles } from 'tss-react/mui';

import { PlaybookAddButton } from './playbook-add-button';
import { RuleCategoryGroup } from './RuleCategoryGroup';
import { RuleRow } from './RuleRow';
import type { PlaybookDetail } from './types';
import { usePlaybookDragAndDrop } from './use-playbook-drag-and-drop';
import { usePlaybookRuleEditing } from './use-playbook-rule-editing';

const useStyles = makeStyles()(theme => ({
  addHeaderButton: {
    flexShrink: 0,
  },
  emptyState: {
    alignItems: 'center',
    border: `1px dashed ${theme.appColors.rule}`,
    borderRadius: 8,
    color: theme.appColors.secondaryType,
    display: 'flex',
    flexDirection: 'column',
    fontSize: 14,
    gap: theme.spacing(0.5),
    padding: theme.spacing(4, 3),
    textAlign: 'center',
  },
  emptyStateTitle: {
    color: theme.appColors.iconsType,
    fontSize: 16,
    fontWeight: 600,
  },
  nextCategory: {
    border: `1px dashed ${theme.appColors.rule}`,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  sectionHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    minWidth: 0,
    width: '100%',
  },
  sectionHeaderLabel: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  sectionIcon: {
    flexShrink: 0,
  },
}));

interface RulesSectionProps {
  onSelectRule: (ruleId: UUID | null) => void;
  playbook: PlaybookDetail;
  selectedRuleId: UUID | null;
  disabled?: boolean;
  /**
   * A generation is still writing. The open category (if any) shows rule
   * skeleton; otherwise a generic next-category card stands in until the
   * next start arrives.
   */
  isGenerating?: boolean;
  openCategoryId?: UUID | null;
}

/**
 * The rules of a playbook, grouped under its categories. What each edit writes —
 * and what it does to the selection — lives in `usePlaybookRuleEditing`; what a
 * drag does to the order, and what a drop sends, lives in
 * `usePlaybookDragAndDrop`.
 */
export const RulesSection = React.memo(function RulesSection({
  disabled = false,
  isGenerating = false,
  onSelectRule,
  openCategoryId = null,
  playbook,
  selectedRuleId,
}: RulesSectionProps): JSX.Element {
  const { classes } = useStyles();
  const { addCategory, addRule, deleteCategory, deleteRule, renameCategory } =
    usePlaybookRuleEditing({ onSelectRule, playbook, selectedRuleId });
  const {
    activeRule,
    collisionDetection,
    handleDragCancel,
    handleDragEnd,
    handleDragOver,
    handleDragStart,
    rulesByCategoryId,
    sensors,
  } = usePlaybookDragAndDrop(playbook);

  const { categories } = playbook;
  /**
   * Which category's name should take focus, and a counter so the same
   * category can be asked again: "Add category" hands back the existing unnamed
   * category while one exists, and that one is already rendered.
   */
  const [focusRequest, setFocusRequest] = useState<{ categoryId: UUID; seq: number } | null>(null);

  const handleAddCategory = useCallback(async (): Promise<void> => {
    const categoryId = await addCategory();
    if (categoryId) setFocusRequest(prev => ({ categoryId, seq: (prev?.seq ?? 0) + 1 }));
  }, [addCategory]);

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <div className={classes.sectionHeaderLabel}>
          <LayoutList className={classes.sectionIcon} size={16} />
          <Typography variant="subtitle1">Categories</Typography>
        </div>
        <AlineTooltip arrow placement="top" title="Add a new category">
          <IconButton
            className={classes.addHeaderButton}
            data-cy={Select.playbooks.addCategoryHeader}
            disabled={disabled}
            size="small"
            onClick={() => void handleAddCategory()}
          >
            <CirclePlus size={16} />
          </IconButton>
        </AlineTooltip>
      </div>
      <DndContext
        collisionDetection={collisionDetection}
        /**
         * Re-measured throughout the drag, because a rule crossing into another
         * category changes the height of both cards and shifts every card below.
         */
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        sensors={sensors}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
      >
        {/* The outer sortable list is the categories; each card holds its own
            sortable list of rules. Vertical-list strategy rather than rect
            sorting because the cards differ in height: siblings shift by the
            dragged card's height, which is exactly the gap it leaves. */}
        <SortableContext
          items={categories.map(category => category.id)}
          strategy={verticalListSortingStrategy}
        >
          {categories.map(category => (
            <RuleCategoryGroup
              key={category.id}
              category={category}
              disabled={disabled}
              focusNameSeq={category.id === focusRequest?.categoryId ? focusRequest.seq : 0}
              isGeneratingOpen={Boolean(isGenerating && category.id === openCategoryId)}
              rules={rulesByCategoryId[category.id] ?? []}
              selectedRuleId={selectedRuleId}
              onAddRule={() => void addRule(category.id)}
              onDeleteCategory={() => void deleteCategory(category.id)}
              onDeleteRule={ruleId => void deleteRule(category.id, ruleId)}
              onRenameCategory={title => void renameCategory(category.id, title)}
              onSelectRule={onSelectRule}
            />
          ))}
        </SortableContext>

        {/* The row under the pointer. A separate copy rather than the list's
            own row, because that row moves between category cards mid-drag
            and a drag cannot follow an element that is remounted. */}
        <DragOverlay>
          {activeRule && (
            <RuleRow overlay selected={activeRule.id === selectedRuleId} title={activeRule.title} />
          )}
        </DragOverlay>
      </DndContext>

      {isGenerating && !openCategoryId && (
        <div className={classes.nextCategory}>
          <Skeleton height={18} variant="text" width="40%" />
          <Skeleton height={18} variant="text" width="70%" />
          <Skeleton height={18} variant="text" width="55%" />
        </div>
      )}

      {categories.length === 0 && !isGenerating && (
        <div className={classes.emptyState} data-cy={Select.playbooks.emptyState}>
          <div className={classes.emptyStateTitle}>No categories yet</div>
          <div>
            A playbook is made up of categories, each holding its rules. Add a category to get
            started.
          </div>
        </div>
      )}

      <PlaybookAddButton
        dataCy={Select.playbooks.addCategory}
        disabled={disabled}
        onClick={() => void handleAddCategory()}
      >
        Add Category
      </PlaybookAddButton>
    </div>
  );
});
